import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted } from 'vue';
import { detectPlatform, getDeviceInfo, type DeviceInfo } from '@/utils/device';
import { getStorage, STORAGE_KEYS } from '@/utils/storage';

interface Row {
  label: string;
  value: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'OnScreenDebuggerGeneralTab',
  setup(__props, { expose: __expose }) {
  __expose();

const loading = ref(true);
const rows = ref<Row[]>([]);

function appVersion(): string {
  if (typeof document === 'undefined') return 'N/A';
  const meta = document.querySelector('meta[name="app-version"]');
  return (meta && (meta as HTMLMetaElement).content) || (process.env.VUE_APP_VERSION as string) || 'N/A';
}

function userId(): string {
  const userInfo = getStorage<{ firebase_id?: string; id?: string }>(STORAGE_KEYS.USER_INFO);
  if (userInfo && typeof userInfo === 'object') return userInfo.firebase_id || userInfo.id || 'N/A';
  return 'Not Logged In';
}

function setRows(items: Row[]) {
  rows.value = items;
}

async function loadWebOS() {
  const win = typeof window !== 'undefined' ? (window as any) : undefined;
  let deviceModel = 'Web-Emulator-Model';
  let deviceId = 'Web-Emulator-Device-ID';

  if (win?.webOS?.service?.request) {
    const webOS = win.webOS;
    await new Promise<void>(resolve => {
      webOS.service.request('luna://com.webos.service.sm', {
        method: 'deviceid/getIDs',
        parameters: { idType: ['LGUDID'] },
        onSuccess: (r: { idList?: Array<{ idValue?: string }> }) => {
          if (r?.idList?.[0]?.idValue) deviceId = r.idList[0].idValue;
          resolve();
        },
        onFailure: () => resolve(),
      });
    });
    await new Promise<void>(resolve => {
      webOS.service.request('luna://com.webos.service.tv.systemproperty', {
        method: 'getSystemInfo',
        parameters: { keys: ['modelName'] },
        onComplete: (r: { returnValue?: boolean; modelName?: string }) => {
          if (r?.returnValue && r.modelName) deviceModel = r.modelName;
          resolve();
        },
      });
    });
  }

  setRows([
    { label: 'Device Model', value: deviceModel },
    { label: 'Brand', value: 'LG' },
    { label: 'UUID', value: deviceId },
    { label: 'App Version', value: appVersion() },
    { label: 'User ID', value: userId() },
  ]);
}

function loadTizen() {
  const win = typeof window !== 'undefined' ? (window as any) : undefined;
  let realModel = 'Web-Emulator-Real-Model';
  let modelCode = 'Web-Emulator-Model';
  let licensedVendor = 'Web-Emulator-Vendor';
  let deviceUUID = 'Web-Emulator-Device-UUID';
  let osVersion = 'Web-Emulator-OS-Version';

  try {
    if (win?.webapis?.productinfo) {
      const pi = win.webapis.productinfo;
      realModel = pi.getRealModel?.() ?? realModel;
      modelCode = pi.getModelCode?.() ?? modelCode;
      licensedVendor = pi.getLicensedVendor?.() ?? licensedVendor;
    }
  } catch {
    // keep fallbacks
  }

  try {
    const matches = navigator.userAgent.match(/\w*Tizen\w*([^)|;]+)/g);
    if (matches) osVersion = matches.join('').split(' ')[1] ?? osVersion;
    if (osVersion !== 'Web-Emulator-OS-Version' && Number(osVersion) < 6) licensedVendor = 'samsung';
  } catch {
    // keep fallback
  }

  try {
    if (win?.tizen?.systeminfo?.getCapabilities) {
      const cap = win.tizen.systeminfo.getCapabilities();
      if (cap?.duid) deviceUUID = cap.duid;
    }
  } catch {
    // keep fallback
  }

  setRows([
    { label: 'Device Model', value: realModel },
    { label: 'Model Code', value: modelCode },
    { label: 'UUID', value: deviceUUID },
    { label: 'OS Name', value: 'Tizen' },
    { label: 'OS Version', value: osVersion },
    { label: 'Licensed Vendor', value: licensedVendor },
    { label: 'App Version', value: appVersion() },
    { label: 'User ID', value: userId() },
  ]);
}

function loadVidaa() {
  const win = typeof window !== 'undefined' ? (window as any) : undefined;
  let modelName = 'Web-Emulator-Model';
  let brand = 'Web-Emulator-Brand';
  let deviceId = 'Web-Emulator-Device-ID';

  try {
    if (typeof win?.Hisense_GetModelName === 'function') modelName = win.Hisense_GetModelName();
    if (typeof win?.Hisense_GetBrand === 'function') brand = win.Hisense_GetBrand();
    if (typeof win?.Hisense_GetDeviceID === 'function') deviceId = win.Hisense_GetDeviceID();
  } catch {
    // keep fallbacks
  }

  setRows([
    { label: 'Model', value: modelName },
    { label: 'Brand', value: brand },
    { label: 'UUID', value: deviceId },
    { label: 'App Version', value: appVersion() },
    { label: 'User ID', value: userId() },
  ]);
}

/** Derive a friendly device model label for web/emulator when platform is unknown */
function fallbackDeviceModel(): string {
  if (process.env.NODE_ENV === 'development') return 'Chrome (development)';
  if (typeof navigator === 'undefined') return 'Unknown Device';
  const ua = navigator.userAgent;
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edg')) return 'Edge';
  return 'Unknown Device';
}

async function loadUnknown() {
  const deviceInfo: DeviceInfo = await getDeviceInfo(true);
  const modelName =
    deviceInfo.modelName && deviceInfo.modelName.trim() !== '' ? deviceInfo.modelName : fallbackDeviceModel();
  const screenStr =
    deviceInfo.screenWidth != null && deviceInfo.screenHeight != null
      ? `${deviceInfo.screenWidth} × ${deviceInfo.screenHeight}`
      : null;

  const items: Row[] = [
    { label: 'Platform', value: 'Unknown (web or emulator)' },
    { label: 'Device Model', value: modelName },
  ];
  if (deviceInfo.manufacturer) items.push({ label: 'Brand', value: deviceInfo.manufacturer });
  if (deviceInfo.deviceId) items.push({ label: 'UUID', value: deviceInfo.deviceId });
  if (screenStr) items.push({ label: 'Screen', value: screenStr });
  items.push({ label: 'App Version', value: appVersion() });
  items.push({ label: 'User ID', value: userId() });

  setRows(items);
}

onMounted(async () => {
  const platform = detectPlatform();
  try {
    if (platform === 'webos') await loadWebOS();
    else if (platform === 'tizen') loadTizen();
    else if (platform === 'vidaa') loadVidaa();
    else await loadUnknown();
  } catch (e) {
    rows.value = [{ label: 'Error', value: String(e) }];
  } finally {
    loading.value = false;
  }
});

const __returned__ = { loading, rows, appVersion, userId, setRows, loadWebOS, loadTizen, loadVidaa, fallbackDeviceModel, loadUnknown }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})